import type { ForcedMovementProviderSourcesInput, ForcedMovementPresenceProvider } from '../types';
import { authorsPresenceLayer } from './presence-layer-authorship';
import { collectForcedMovementSourceCandidates } from './forced-movement-source-collection';

/** Compile source provenance from resolved providers, preserving PHP's discovery order and exact pointers. */
export function collectForcedMovementProviderSources(input: ForcedMovementProviderSourcesInput): Record<string, string>[] {
    if (input.layerName !== 'force_move_resistance' && input.layerName !== 'immune_to_forced_movement') return [];
    const candidates: Record<string, string | null>[] = [];
    const authors = (provider: ForcedMovementPresenceProvider): boolean => authorsPresenceLayer(provider.presence);
    if (authors(input.host)) {
        candidates.push({ source_item_id: input.host.itemId,
            source_name: input.host.itemName ?? 'Creature', source_kind: input.host.itemType ?? 'creature' });
    }
    for (const carrier of input.carriers) {
        if (carrier === null || !authors(carrier)) continue;
        candidates.push({ source_item_id: carrier.itemId,
            source_name: carrier.itemName ?? 'Creature classification', source_kind: carrier.itemType ?? 'carrier' });
    }
    for (const source of input.generalSources) {
        const kind = source.kind ?? '';
        if (kind === 'status_effect' || kind === 'creature_type') continue;
        candidates.push({ source_item_id: source.itemId, source_name: source.name ?? 'Defense source',
            source_kind: kind !== '' ? kind : 'ability', source_inventory_id: source.inventoryId, source_thumb_url: source.thumbUrl });
    }
    for (const status of input.statuses) {
        const definition = status.definition ?? status.entry;
        if (!authors(definition)) continue;
        candidates.push({ source_item_id: status.entry.itemId ?? definition.itemId,
            source_name: status.entry.itemName ?? definition.itemName ?? 'Status Effect', source_kind: 'status_effect',
            source_status_id: status.statusId, source_origin_item_id: status.originId, source_origin_name: status.originName });
    }
    for (const { feat, granted } of input.feats) {
        if (authors(feat)) candidates.push({ source_item_id: feat.itemId, source_name: feat.itemName ?? 'Feat', source_kind: 'feat' });
        for (const ability of granted) {
            if (!authors(ability)) continue;
            candidates.push({ source_item_id: ability.itemId, source_name: ability.itemName ?? 'Granted ability',
                source_kind: ability.itemType ?? 'ability', source_origin_item_id: feat.itemId, source_origin_name: feat.itemName });
        }
    }
    return collectForcedMovementSourceCandidates({ layerName: input.layerName, candidates, subtypeKinds: input.subtypeKinds });
}
